import dayjs                      from 'dayjs';
import currentEditorStore         from '@/TokamapEditor/stores/currentEditorStore';
import currentProjectEditorStore  from '@/TokamapEditor/stores/currentProjectEditorStore';
import authorNameStore            from '@/TokamapEditor/stores/authorNameStore';
import { getIdFromUrn }           from '@widesk-core/tools/identifier';

/**
 * Formatage partagé de "la dernière modification", pour que la barre du haut (`SaveStatusBanner`) et
 * le menu des cartes (`MapMenu`) disent exactement la même chose de la même façon — ils lisent la
 * même donnée (`ProjectEditorStore.lastModificationByMapUrn`), l'un le maximum du projet, l'autre la
 * valeur de chaque carte.
 *
 * Toutes ces fonctions lisent des observables : leurs appelants doivent être des `observer`.
 */

/**
 * "Modifié à 14:32" le jour même, "Modifié le 12/09" au-delà. Le jour est cité dès qu'on sort de la
 * date du jour : "à 14:32" tout court se lirait comme "il y a quelques minutes" à l'ouverture d'un
 * projet laissé de côté depuis une semaine.
 */
export function lastModificationLabel(at: Date): string {
	const day = dayjs(at);

	return day.isSame(dayjs(), 'day') ? `Modifié à ${day.format('LT')}` : `Modifié le ${day.format('DD/MM')}`;
}

/** Date complète, secondes comprises : deux modifications rapprochées doivent se distinguer. */
export function lastModificationFullDate(at: Date): string {
	return dayjs(at).format('L [à] LTS');
}

/**
 * Nom à afficher pour l'auteur d'une modification. Les utilisateurs connectés en ce moment d'abord —
 * on a déjà leur identité sous la main, sans requête — puis `authorNameStore`, qui sait résoudre
 * n'importe quelle URN et couvre donc ceux qui ont modifié puis quitté l'éditeur.
 *
 * `undefined` pendant la fraction de seconde que dure cette résolution, et si elle échoue (compte
 * supprimé, droits insuffisants) : l'appelant dit alors "un autre utilisateur", ce qui reste vrai.
 */
export function userName(userUrn: Urn): string | undefined {
	const connected = connectedUser(userUrn);

	if (connected) return [connected.firstName, connected.lastName].filter(Boolean).join(' ').trim() || undefined;

	return authorNameStore.get(userUrn);
}

/**
 * Couleur de l'auteur, la même que son avatar dans la barre du haut et que son curseur sur le plan.
 * Repli discret pour un auteur qu'on n'arrive pas à identifier.
 */
export function userColor(userUrn: Urn): string {
	return connectedUser(userUrn)?.color || 'rgba(255, 255, 255, 0.45)';
}

/** Le titre de la carte visée, parmi celles du projet — `undefined` si elle n'y est plus. */
export function mapLabel(mapUrn?: Urn): string | undefined {
	if (!mapUrn) return undefined;

	return currentProjectEditorStore.get?.mapCollection.models.find(map => map.urn === mapUrn)?.label || undefined;
}

function connectedUser(userUrn: Urn) {
	const userId = getIdFromUrn(userUrn);

	return currentEditorStore.editorUserStore.users.find(u => String(u.id) === String(userId));
}
